//! Client-side model for Excel's Page Setup dialog.
//!
//! The Rust core (`PdfExportOptions` / `PageSetup`) already accepts every
//! option here; this module holds the editable form state and converts it to
//! the camelCase payload the core deserializes. Numeric fields are kept as
//! strings so partially-typed values ("0.", "") survive editing, and are
//! parsed on export.

export type PaperSizeId = 'letter' | 'legal' | 'tabloid' | 'ledger' | 'executive' | 'a3' | 'a4' | 'a5' | 'b4' | 'b5';
export type OrientationId = 'portrait' | 'landscape';
export type ScalingChoice = 'normal' | 'fitWidth' | 'fitPage' | 'fitPages' | 'custom';
export type PageOrderId = 'downThenOver' | 'overThenDown';
export type SheetScope = 'all' | 'current';

export const PAPER_SIZES: { value: PaperSizeId; label: string }[] = [
  { value: 'letter', label: 'Letter (8.5" × 11")' },
  { value: 'legal', label: 'Legal (8.5" × 14")' },
  { value: 'tabloid', label: 'Tabloid (11" × 17")' },
  { value: 'ledger', label: 'Ledger (17" × 11")' },
  { value: 'executive', label: 'Executive (7.25" × 10.5")' },
  { value: 'a3', label: 'A3' },
  { value: 'a4', label: 'A4' },
  { value: 'a5', label: 'A5' },
  { value: 'b4', label: 'B4' },
  { value: 'b5', label: 'B5' },
];

/** One header or footer position; each may contain Excel codes (&P, &N, …). */
export interface HeaderFooterSections {
  left: string;
  center: string;
  right: string;
}

export interface HeaderFooterState {
  odd: HeaderFooterSections;
  /** Used for even pages when `differentOddEven` is on. */
  even: HeaderFooterSections;
  /** Used for the first page when `differentFirst` is on. */
  first: HeaderFooterSections;
  differentOddEven: boolean;
  differentFirst: boolean;
  scaleWithDoc: boolean;
  alignWithMargins: boolean;
}

export interface MarginsState {
  left: string;
  right: string;
  top: string;
  bottom: string;
  header: string;
  footer: string;
}

export interface PageSetupState {
  sheetScope: SheetScope;
  paperSize: PaperSizeId;
  orientation: OrientationId;
  scaling: ScalingChoice;
  customScale: string;
  fitPagesWide: string;
  fitPagesTall: string;
  /** Excel's "First page number"; empty means Auto. */
  firstPageNumber: string;
  margins: MarginsState;
  centerHorizontally: boolean;
  centerVertically: boolean;
  header: HeaderFooterState;
  footer: HeaderFooterState;
  printArea: string;
  /** Excel's "Rows to repeat at top", e.g. `1:2`. */
  repeatRows: string;
  /** Excel's "Columns to repeat at left", e.g. `A:B`. */
  repeatCols: string;
  printGridlines: boolean;
  printHeadings: boolean;
  pageOrder: PageOrderId;
}

const EMPTY_SECTIONS: HeaderFooterSections = { left: '', center: '', right: '' };

const defaultHeaderFooter = (): HeaderFooterState => ({
  odd: { ...EMPTY_SECTIONS },
  even: { ...EMPTY_SECTIONS },
  first: { ...EMPTY_SECTIONS },
  differentOddEven: false,
  differentFirst: false,
  scaleWithDoc: true,
  alignWithMargins: true,
});

/** Excel's margin presets (inches). */
export const MARGIN_PRESETS: { id: string; label: string; margins: MarginsState }[] = [
  {
    id: 'normal',
    label: 'Normal',
    margins: { left: '0.7', right: '0.7', top: '0.75', bottom: '0.75', header: '0.3', footer: '0.3' },
  },
  {
    id: 'wide',
    label: 'Wide',
    margins: { left: '1', right: '1', top: '1', bottom: '1', header: '0.5', footer: '0.5' },
  },
  {
    id: 'narrow',
    label: 'Narrow',
    margins: { left: '0.25', right: '0.25', top: '0.75', bottom: '0.75', header: '0.3', footer: '0.3' },
  },
];

export const HEADER_FOOTER_PRESETS: { id: string; label: string; sections: HeaderFooterSections }[] = [
  { id: 'none', label: '(none)', sections: { ...EMPTY_SECTIONS } },
  { id: 'page', label: 'Page 1', sections: { left: '', center: 'Page &P', right: '' } },
  { id: 'pageOf', label: 'Page 1 of ?', sections: { left: '', center: 'Page &P of &N', right: '' } },
  { id: 'sheetName', label: 'Sheet name', sections: { left: '', center: '&A', right: '' } },
  { id: 'fileName', label: 'File name', sections: { left: '', center: '&F', right: '' } },
  {
    id: 'fileDatePage',
    label: 'File name, date, page',
    sections: { left: '&F', center: '&D', right: 'Page &P' },
  },
];

export const defaultPageSetupState = (): PageSetupState => ({
  sheetScope: 'all',
  paperSize: 'letter',
  orientation: 'portrait',
  scaling: 'normal',
  customScale: '100',
  fitPagesWide: '1',
  fitPagesTall: '1',
  firstPageNumber: '',
  margins: { ...MARGIN_PRESETS[0].margins },
  centerHorizontally: false,
  centerVertically: false,
  header: defaultHeaderFooter(),
  footer: defaultHeaderFooter(),
  printArea: '',
  repeatRows: '',
  repeatCols: '',
  printGridlines: false,
  printHeadings: false,
  pageOrder: 'downThenOver',
});

/** Margins are inches; a blank or invalid entry falls back to `fallback`. */
const parseInches = (value: string, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

/** Preset id matching the current values, or 'custom' when none matches. */
export const marginPresetId = (margins: MarginsState): string => {
  const keys = Object.keys(margins) as (keyof MarginsState)[];
  const preset = MARGIN_PRESETS.find((candidate) =>
    keys.every((key) => parseInches(candidate.margins[key]) === parseInches(margins[key]))
  );
  return preset?.id ?? 'custom';
};

export const headerFooterPresetId = (sections: HeaderFooterSections): string =>
  HEADER_FOOTER_PRESETS.find(
    (preset) =>
      preset.sections.left === sections.left &&
      preset.sections.center === sections.center &&
      preset.sections.right === sections.right
  )?.id ?? 'custom';

// --- payload types, mirroring the core's serde model -------------------------

type PdfMarginsPayload = {
  left: number;
  right: number;
  top: number;
  bottom: number;
  header: number;
  footer: number;
};

type PdfScalingPayload =
  | { type: 'zoom'; percent: number }
  | { type: 'fitTo'; width: number | null; height: number | null };

type PdfHeaderFooterPayload = {
  odd: HeaderFooterSections;
  even?: HeaderFooterSections;
  first?: HeaderFooterSections;
  scaleWithDoc: boolean;
  alignWithMargins: boolean;
};

export type PdfPageSetupPayload = {
  paperSize: PaperSizeId;
  orientation: OrientationId;
  margins: PdfMarginsPayload;
  scaling: PdfScalingPayload;
  pageOrder: PageOrderId;
  centerHorizontally: boolean;
  centerVertically: boolean;
  printGridlines: boolean;
  printHeadings: boolean;
  header: PdfHeaderFooterPayload;
  footer: PdfHeaderFooterPayload;
  firstPageNumber?: number;
};

type PdfSheetOptionsPayload = {
  /** Page setup for this sheet alone, overriding the export-wide one. */
  pageSetup?: PdfPageSetupPayload;
  printArea?: string;
  repeatRows?: [number, number];
  repeatCols?: [number, number];
};

/**
 * The core's persisted `SheetPrintSetup`, sent as JSON on `SheetInfo` and
 * accepted back by `setSheetPrintSetup`. Breaks are absolute 1-based indices,
 * as in Excel.
 */
export type SheetPrintSetupPayload = {
  pageSetup: PdfPageSetupPayload;
  printArea?: string | null;
  repeatRows?: [number, number] | null;
  repeatCols?: [number, number] | null;
  rowBreaks: number[];
  colBreaks: number[];
};

export type PdfExportOptionsPayload = {
  fileName: string;
  sheetIds?: string[];
  /**
   * Applies to sheets with no setup of their own. Omitting it leaves them on
   * the core's Excel defaults.
   */
  pageSetup?: PdfPageSetupPayload;
  sheetOptions?: Record<string, PdfSheetOptionsPayload>;
};

// --- parsing -----------------------------------------------------------------

const clampScale = (value: string): number => {
  const parsed = Math.round(Number(value));
  if (!Number.isFinite(parsed)) return 100;
  return Math.min(400, Math.max(10, parsed));
};

/** Empty means automatic (unconstrained) in that axis, as in Excel. */
const parseFitPages = (value: string): number | null => {
  const parsed = Math.round(Number(value));
  return value.trim() !== '' && Number.isFinite(parsed) && parsed >= 1 ? parsed : null;
};

const buildScaling = (state: PageSetupState): PdfScalingPayload => {
  switch (state.scaling) {
    case 'fitWidth':
      return { type: 'fitTo', width: 1, height: null };
    case 'fitPage':
      return { type: 'fitTo', width: 1, height: 1 };
    case 'fitPages':
      return { type: 'fitTo', width: parseFitPages(state.fitPagesWide), height: parseFitPages(state.fitPagesTall) };
    case 'custom':
      return { type: 'zoom', percent: clampScale(state.customScale) };
    default:
      return { type: 'zoom', percent: 100 };
  }
};

/** `A` → 1, `Z` → 26, `AA` → 27. Returns undefined for non-letters. */
export const columnLetterToNumber = (letters: string): number | undefined => {
  const trimmed = letters.trim().toUpperCase();
  if (!/^[A-Z]+$/.test(trimmed)) return undefined;
  let result = 0;
  for (const char of trimmed) {
    result = result * 26 + (char.charCodeAt(0) - 64);
  }
  return result;
};

/**
 * Excel's print-title syntax for rows: `1:2`, `$1:$2`, or a single `1`.
 * Returns the 1-based inclusive pair the core expects.
 */
export const parseRepeatRows = (value: string): [number, number] | undefined => {
  const parts = value.replace(/\$/g, '').trim().split(':');
  if (parts.length === 0 || parts.length > 2 || parts[0] === '') return undefined;
  const bounds = parts.map((part) => Number(part.trim()));
  if (bounds.some((bound) => !Number.isInteger(bound) || bound < 1)) return undefined;
  const [start, end = start] = bounds;
  return start <= end ? [start, end] : [end, start];
};

/** Excel's print-title syntax for columns: `A:B`, `$A:$B`, or a single `A`. */
export const parseRepeatCols = (value: string): [number, number] | undefined => {
  const parts = value.replace(/\$/g, '').trim().split(':');
  if (parts.length === 0 || parts.length > 2 || parts[0] === '') return undefined;
  const bounds = parts.map((part) => columnLetterToNumber(part));
  if (bounds.some((bound) => bound === undefined)) return undefined;
  const [start, end = start] = bounds as number[];
  return start <= end ? [start, end] : [end, start];
};

const buildHeaderFooter = (state: HeaderFooterState): PdfHeaderFooterPayload => ({
  odd: state.odd,
  ...(state.differentOddEven ? { even: state.even } : {}),
  ...(state.differentFirst ? { first: state.first } : {}),
  scaleWithDoc: state.scaleWithDoc,
  alignWithMargins: state.alignWithMargins,
});

/**
 * Per-sheet options (print area and print titles) apply to one sheet, as in
 * Excel where Page Setup acts on the active sheet. Manual breaks are omitted
 * because this dialog doesn't own them; the core then exports with the breaks
 * persisted on the sheet.
 */
const buildSheetOptions = (state: PageSetupState): PdfSheetOptionsPayload => {
  const printArea = state.printArea.trim();
  const repeatRows = parseRepeatRows(state.repeatRows);
  const repeatCols = parseRepeatCols(state.repeatCols);
  return {
    ...(printArea !== '' ? { printArea } : {}),
    ...(repeatRows ? { repeatRows } : {}),
    ...(repeatCols ? { repeatCols } : {}),
  };
};

export const buildPageSetupPayload = (state: PageSetupState): PdfPageSetupPayload => {
  const firstPageNumber = Math.round(Number(state.firstPageNumber));

  return {
    paperSize: state.paperSize,
    orientation: state.orientation,
    margins: {
      left: parseInches(state.margins.left),
      right: parseInches(state.margins.right),
      top: parseInches(state.margins.top),
      bottom: parseInches(state.margins.bottom),
      header: parseInches(state.margins.header),
      footer: parseInches(state.margins.footer),
    },
    scaling: buildScaling(state),
    pageOrder: state.pageOrder,
    centerHorizontally: state.centerHorizontally,
    centerVertically: state.centerVertically,
    printGridlines: state.printGridlines,
    printHeadings: state.printHeadings,
    header: buildHeaderFooter(state.header),
    footer: buildHeaderFooter(state.footer),
    ...(state.firstPageNumber.trim() !== '' && Number.isFinite(firstPageNumber) ? { firstPageNumber } : {}),
  };
};

/**
 * The dialog's settings belong to the current sheet, as in Excel where Page
 * Setup acts on the active sheet, so they are sent as that sheet's options
 * rather than as the export-wide page setup. An all-sheets export then prints
 * every other sheet with its own saved setup instead of this one's.
 */
export const buildPdfExportOptions = (
  state: PageSetupState,
  { fileName, sheetId }: { fileName: string; sheetId: string }
): PdfExportOptionsPayload => ({
  fileName,
  ...(state.sheetScope === 'current' ? { sheetIds: [sheetId] } : {}),
  sheetOptions: { [sheetId]: { pageSetup: buildPageSetupPayload(state), ...buildSheetOptions(state) } },
});

/**
 * The setup to persist on the sheet. Manual breaks are carried through
 * untouched: they are set by the page-break preview, not this dialog.
 */
export const buildSheetPrintSetup = (
  state: PageSetupState,
  breaks: { rowBreaks: number[]; colBreaks: number[] }
): SheetPrintSetupPayload => {
  const sheetOptions = buildSheetOptions(state);

  return {
    pageSetup: buildPageSetupPayload(state),
    printArea: sheetOptions.printArea ?? null,
    repeatRows: sheetOptions.repeatRows ?? null,
    repeatCols: sheetOptions.repeatCols ?? null,
    rowBreaks: breaks.rowBreaks,
    colBreaks: breaks.colBreaks,
  };
};

type ScalingState = Pick<PageSetupState, 'scaling' | 'customScale' | 'fitPagesWide' | 'fitPagesTall'>;

const scalingToState = (scaling: PdfScalingPayload): ScalingState => {
  const { customScale, fitPagesWide, fitPagesTall } = defaultPageSetupState();
  const defaults = { customScale, fitPagesWide, fitPagesTall };

  if (scaling.type === 'zoom') {
    return scaling.percent === 100
      ? { ...defaults, scaling: 'normal' }
      : { ...defaults, scaling: 'custom', customScale: String(scaling.percent) };
  }
  const { width, height } = scaling;
  if (width === 1 && height === null) return { ...defaults, scaling: 'fitWidth' };
  if (width === 1 && height === 1) return { ...defaults, scaling: 'fitPage' };
  return {
    ...defaults,
    scaling: 'fitPages',
    fitPagesWide: width === null ? '' : String(width),
    fitPagesTall: height === null ? '' : String(height),
  };
};

const headerFooterToState = (payload: PdfHeaderFooterPayload | undefined): HeaderFooterState => {
  const state = defaultHeaderFooter();
  if (!payload) return state;
  return {
    odd: payload.odd ?? state.odd,
    even: payload.even ?? state.even,
    first: payload.first ?? state.first,
    differentOddEven: !!payload.even,
    differentFirst: !!payload.first,
    scaleWithDoc: payload.scaleWithDoc ?? state.scaleWithDoc,
    alignWithMargins: payload.alignWithMargins ?? state.alignWithMargins,
  };
};

const rowRangeToString = (range: [number, number] | null | undefined): string => {
  if (!range) return '';
  const [start, end] = range;
  return start === end ? `${start}` : `${start}:${end}`;
};

/** 1 → `A`, 27 → `AA`. */
export const columnNumberToLetter = (value: number): string => {
  let result = '';
  let remaining = Math.max(1, Math.round(value));
  while (remaining > 0) {
    const index = (remaining - 1) % 26;
    result = String.fromCharCode(65 + index) + result;
    remaining = Math.floor((remaining - 1) / 26);
  }
  return result;
};

const colRangeToString = (range: [number, number] | null | undefined): string => {
  if (!range) return '';
  const [start, end] = range;
  const from = columnNumberToLetter(start);
  return start === end ? from : `${from}:${columnNumberToLetter(end)}`;
};

/** Hydrates the dialog's form state from a sheet's persisted print setup. */
export const pageSetupStateFromPrintSetup = (
  setup: SheetPrintSetupPayload | undefined,
  sheetScope: SheetScope = 'all'
): PageSetupState => {
  const defaults = defaultPageSetupState();
  if (!setup) return { ...defaults, sheetScope };

  const pageSetup: Partial<PdfPageSetupPayload> = setup.pageSetup ?? {};
  const margins = pageSetup.margins;

  return {
    ...defaults,
    sheetScope,
    paperSize: pageSetup.paperSize ?? defaults.paperSize,
    orientation: pageSetup.orientation ?? defaults.orientation,
    ...scalingToState(pageSetup.scaling ?? { type: 'zoom', percent: 100 }),
    firstPageNumber: pageSetup.firstPageNumber === undefined ? '' : String(pageSetup.firstPageNumber),
    margins: margins
      ? {
          left: String(margins.left),
          right: String(margins.right),
          top: String(margins.top),
          bottom: String(margins.bottom),
          header: String(margins.header),
          footer: String(margins.footer),
        }
      : defaults.margins,
    centerHorizontally: pageSetup.centerHorizontally ?? defaults.centerHorizontally,
    centerVertically: pageSetup.centerVertically ?? defaults.centerVertically,
    header: headerFooterToState(pageSetup.header),
    footer: headerFooterToState(pageSetup.footer),
    printArea: setup.printArea ?? '',
    repeatRows: rowRangeToString(setup.repeatRows),
    repeatCols: colRangeToString(setup.repeatCols),
    printGridlines: pageSetup.printGridlines ?? defaults.printGridlines,
    printHeadings: pageSetup.printHeadings ?? defaults.printHeadings,
    pageOrder: pageSetup.pageOrder ?? defaults.pageOrder,
  };
};
